import { ReactElement, useEffect, useState } from "react";

import {
  PromptSuggestionCategory,
  WiseOwlBlock,
  WiseOwlBlockType,
  WiseOwlEventEnum,
  WiseOwlIngress,
} from "@twiliointernal/e2-commons-shared-definitions";

import { useGetHome, useGetHcaInsights, useWiseOwlContext } from "../../hooks";

import { PromptSuggestionsLoader } from "./sub";
import BlockRenderer from "../blockRenderer";

const PromptSuggestionsContainer = (): ReactElement => {
  const [blocks, setBlocks] = useState<WiseOwlBlock[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { applicationProps, isOneAdmin, user, ingress } = useWiseOwlContext();
  const { mutateAsync: getHomeContent } = useGetHome(applicationProps);
  const { mutateAsync: getHcaInsights } = useGetHcaInsights({
    ingress: applicationProps.ingress,
  });
  const userAccountSid = user?.accountSid;

  const { onError, onEvent } = applicationProps;

  const truncatePromptSuggestions = (blocks: WiseOwlBlock[]) => {
    if (blocks.length === 0) {
      return blocks;
    }
    const promptBlocks = blocks;
    const firstBlock = blocks.values().next().value;

    const blockValues = firstBlock?.values as {
      categories: PromptSuggestionCategory[];
    };
    const categories = [blockValues.categories[1]];
    const truncatedCategories = categories.slice(0, 3);
    (
      promptBlocks[0].values as { categories: PromptSuggestionCategory[] }
    ).categories = truncatedCategories;
    return promptBlocks;
  };

  useEffect(() => {
    /**
     * This condition is specific for One Admin which is not
     * up-to-date with the latest WO feature changes
     */
    if (!isOneAdmin) {
      (async () => await handleGetHomeContent())();
      if (ingress === WiseOwlIngress.osc) {
        handleGetHcaInsights();
      }
    } else {
      setIsLoading(false);
    }
  }, [userAccountSid]);

  const handleGetHomeContent = async () => {
    try {
      const response = await getHomeContent({});

      if (onEvent) {
        onEvent({
          eventType: WiseOwlEventEnum.getPreChatContent,
          data: {},
        });
      }

      const homeBlocks = response.data.blocks;

      if (ingress === WiseOwlIngress.sendGrid) {
        /**
         * Temporal hardcoded values for development and stage
         * TODO: Remove all of this values since they should be validated from the API, not here on the frontend.
         */
        const testUserToSendGridValuesMap: Record<string, any> = {
          "svc-engineering-devs+test__help-center-free-account@twilio.com": {
            userId: "2133496358",
            username:
              "svc-engineering-devs+test__help-center-free-account@twilio.com",
            supportPackage: {
              emailSupportPackage: "sg_email_pkg_free",
              marketingSupportPackage: "sg_mktg_pkg_free",
            },
          },
          "svc-engineering-devs+test__help-center-chat-account@twilio.com": {
            userId: "2133497096",
            username:
              "svc-engineering-devs+test__help-center-chat-account@twilio.com",
            supportPackage: {
              emailSupportPackage: "sg_email_pkg_essentials",
              marketingSupportPackage: "sg_mktg_pkg_advanced",
            },
          },
          "svc-engineering-devs+test__help-center-all@twilio.com": {
            userId: "2133505580",
            username: "svc-engineering-devs+test__help-center-all@twilio.com",
            supportPackage: {
              emailSupportPackage: "sg_email_pkg_essentials",
              marketingSupportPackage: "sg_mktg_pkg_advanced",
            },
          },
        };
        const userEmail: string = (window as any).HelpCenter?.user?.email;
        const devSendGridUser = testUserToSendGridValuesMap[userEmail] || null;
        const mcPlan =
          window.MC_Plan ||
          devSendGridUser?.supportPackage?.marketingSupportPackage ||
          "";
        const eiPlan =
          window.EI_Plan ||
          devSendGridUser?.supportPackage?.emailSupportPackage ||
          "";
        const isPaidUser =
          (!!mcPlan && !mcPlan.endsWith("_pkg_free")) ||
          (!!eiPlan && !eiPlan.endsWith("pkg_free"));
        const sgBlocks = (homeBlocks as WiseOwlBlock[]).filter((block) => {
          if (block.type === WiseOwlBlockType.SupportCta) {
            return isPaidUser;
          }
          return true;
        });
        setBlocks(sgBlocks);
      } else {
        setBlocks(homeBlocks);
      }
    } catch (err) {
      if (onError) {
        onError({
          eventType: WiseOwlEventEnum.getPreChatContent,
          data: {},
          error: err as Error,
        });
      }
    }
    setIsLoading(false);
  };

  const handleGetHcaInsights = async () => {
    try {
      const response = await getHcaInsights({
        ingress: applicationProps.ingress,
      });

      if (onEvent) {
        onEvent({
          eventType: WiseOwlEventEnum.getHcaInsights,
          data: {},
        });
      }

      if (!response?.data?.blocks || response.data.blocks.length === 0) {
        return;
      }

      setBlocks((prevBlocks) => {
        const editedBlocks = truncatePromptSuggestions(prevBlocks);

        const blocksWithInsights = [editedBlocks[0], ...response.data.blocks];

        if (prevBlocks.length > 1) {
          blocksWithInsights.push(prevBlocks[1]);
        }
        return blocksWithInsights;
      });
    } catch (err) {
      if (onError) {
        onError({
          eventType: WiseOwlEventEnum.getPreChatContent,
          data: {},
          error: err as Error,
        });
      }
    }
  };

  if (isLoading) {
    return <PromptSuggestionsLoader />;
  }

  return <BlockRenderer blocks={blocks} />;
};

export default PromptSuggestionsContainer;
